import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import type {
  Event,
  EventType,
  EventUser,
  EventContact,
  EventTag,
  EventStatus,
  CreateEventInput,
  UpdateEventInput,
  CreateEventUserInput,
  AddEventContactInput,
  UpdateEventContactInput,
  CreateEventTagInput,
  UpdateEventTagInput,
  EventVendorRecipient,
  SendVendorEmailInput,
} from '@peles/shared';
import * as eventsApi from '../api/events';

export function useEvents(filters?: { status?: EventStatus; eventTypeId?: string }) {
  return useQuery<Event[]>({
    queryKey: ['events', filters],
    queryFn: () => eventsApi.listEvents(filters),
  });
}

export function useEvent(id: string) {
  return useQuery<Event>({
    queryKey: ['events', id],
    queryFn: () => eventsApi.getEvent(id),
    enabled: !!id,
  });
}

export function useCreateEvent() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateEventInput) => eventsApi.createEvent(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['events'] });
    },
  });
}

export function useUpdateEvent() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, updates }: { id: string; updates: UpdateEventInput }) =>
      eventsApi.updateEvent(id, updates),
    onSuccess: (updatedEvent) => {
      queryClient.setQueryData(['events', updatedEvent.id], updatedEvent);
      queryClient.invalidateQueries({ queryKey: ['events'] });
    },
  });
}

export function useCancelEvent() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => eventsApi.cancelEvent(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['events'] });
    },
  });
}

export function useEventTypes() {
  return useQuery<EventType[]>({
    queryKey: ['event-types'],
    queryFn: () => eventsApi.listEventTypes(),
    staleTime: Infinity,
  });
}

export function useEventUsers(eventId: string) {
  return useQuery<EventUser[]>({
    queryKey: ['events', eventId, 'users'],
    queryFn: () => eventsApi.listEventUsers(eventId),
    enabled: !!eventId,
  });
}

export function useAddEventUser(eventId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateEventUserInput) => eventsApi.addEventUser(eventId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['events', eventId, 'users'] });
    },
  });
}

export function useRemoveEventUser(eventId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (userId: string) => eventsApi.removeEventUser(eventId, userId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['events', eventId, 'users'] });
    },
  });
}

// ─── Event Contacts ─────────────────────────────────────────────────────────

export function useEventContacts(eventId: string) {
  return useQuery<EventContact[]>({
    queryKey: ['events', eventId, 'contacts'],
    queryFn: () => eventsApi.listEventContacts(eventId),
    enabled: !!eventId,
  });
}

export function useAddEventContact(eventId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: AddEventContactInput) => eventsApi.addEventContact(eventId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['events', eventId, 'contacts'] });
    },
  });
}

export function useUpdateEventContact(eventId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ contactId, updates }: { contactId: string; updates: UpdateEventContactInput }) =>
      eventsApi.updateEventContact(eventId, contactId, updates),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['events', eventId, 'contacts'] });
    },
  });
}

export function useRemoveEventContact(eventId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (contactId: string) => eventsApi.removeEventContact(eventId, contactId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['events', eventId, 'contacts'] });
    },
  });
}

// ─── Event Tags ─────────────────────────────────────────────────────────────

export function useEventTags(eventId: string | undefined) {
  return useQuery<EventTag[]>({
    queryKey: ['events', eventId, 'tags'],
    queryFn: () => eventsApi.listEventTags(eventId!),
    enabled: !!eventId,
  });
}

export function useCreateEventTag(eventId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateEventTagInput) => eventsApi.createEventTag(eventId, input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['events', eventId, 'tags'] });
    },
  });
}

export function useUpdateEventTag(eventId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ tagId, updates }: { tagId: string; updates: UpdateEventTagInput }) =>
      eventsApi.updateEventTag(eventId, tagId, updates),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['events', eventId, 'tags'] });
    },
  });
}

export function useDeleteEventTag(eventId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (tagId: string) => eventsApi.deleteEventTag(eventId, tagId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['events', eventId, 'tags'] });
    },
  });
}

// ─── Event Vendor Email ─────────────────────────────────────────────────────

export function useEventVendorRecipients(eventId: string | undefined) {
  return useQuery<EventVendorRecipient[]>({
    queryKey: ['events', eventId, 'vendor-recipients'],
    queryFn: () => eventsApi.getEventVendorRecipients(eventId!),
    enabled: !!eventId,
  });
}

export function useSendVendorEmail(eventId: string) {
  return useMutation({
    mutationFn: (input: SendVendorEmailInput) => eventsApi.sendVendorEmail(eventId, input),
  });
}
